import { forwardRef, type HTMLAttributes } from "react";
import { cn } from "@/lib/utils";
import { Table, TableRow } from "@/components/ui/table";

export const expandedParentRowClassName = "";

type DivProps = HTMLAttributes<HTMLDivElement>;

const nestedWrapperClassName =
  "pb-3 bg-card ml-6 px-4 border-b border-border relative before:absolute before:left-0 before:top-2 before:bottom-2 before:w-0.5 before:bg-primary before:content-['']";

export const NestedDrawerTableContainer = forwardRef<HTMLDivElement, DivProps>(
  ({ className, children, ...props }, ref) => (
    <div ref={ref} className={cn(nestedWrapperClassName, className)} {...props}>
      <Table>{children}</Table>
    </div>
  ),
);
NestedDrawerTableContainer.displayName = "NestedDrawerTableContainer";

export const NestedDrawerContainer = forwardRef<HTMLDivElement, DivProps>(
  ({ className, children, ...props }, ref) => (
    <div ref={ref} className={cn(nestedWrapperClassName, className)} {...props}>
      {children}
    </div>
  ),
);
NestedDrawerContainer.displayName = "NestedDrawerContainer";

type TableRowProps = React.ComponentPropsWithoutRef<typeof TableRow> & {
  flush?: boolean;
};

export const NestedDrawerTableHeaderRow = forwardRef<HTMLTableRowElement, TableRowProps>(
  ({ className, children, flush = false, ...props }, ref) => (
    <TableRow
      ref={ref}
      className={cn(
        "!bg-card hover:!bg-card [&>th]:pt-2 [&>th]:pb-3 [&>th]:border-b [&>th]:border-border-strong",
        !flush && "[&>*:first-child]:pl-4 [&>*:last-child]:pr-4",
        className,
      )}
      {...props}
    >
      {children}
    </TableRow>
  ),
);
NestedDrawerTableHeaderRow.displayName = "NestedDrawerTableHeaderRow";

export const NestedDrawerTableBodyRow = forwardRef<HTMLTableRowElement, TableRowProps>(
  ({ className, children, flush = false, onClick, ...props }, ref) => {
    const clickable = Boolean(onClick);
    return (
      <TableRow
        ref={ref}
        onClick={onClick}
        className={cn(
          "bg-card border-border/50 [&>*]:text-xs",
          clickable
            ? "cursor-pointer hover:bg-muted/40"
            : "hover:bg-muted/40",
          !flush && "[&>*:first-child]:pl-4 [&>*:last-child]:pr-4",
          className,
        )}
        {...props}
      >
        {children}
      </TableRow>
    );
  },
);
NestedDrawerTableBodyRow.displayName = "NestedDrawerTableBodyRow";
